import { supabase } from '../supabase';
import { format } from 'date-fns';
import { coreCall } from './gatewayService';
import { it } from 'date-fns/locale';

// ============================================
// GDPR Service - Art. 17 (Oblio) e Art. 20 (Portabilità)
// ============================================

export interface GDPRExportData {
  export_info: {
    date: string;
    format: 'GDPR_PORTABLE_JSON';
    version: '1.0';
    data_controller: string;
    data_controller_email: string;
  };
  user: {
    id: string;
    email: string;
    full_name: string | null;
    phone: string | null;
    avatar_url: string | null;
    created_at: string;
    updated_at: string;
  };
  organization: {
    id: string;
    name: string;
    role: string;
    joined_at: string;
  } | null;
  preferences: {
    theme?: string;
    language?: string;
    notifications_enabled?: boolean;
    google_account_connected?: boolean;
  };
  consents: {
    privacy_policy: { accepted: boolean; date: string | null; version: string | null };
    terms_of_service: { accepted: boolean; date: string | null };
    marketing_email: { accepted: boolean; date: string | null };
    marketing_sms: { accepted: boolean; date: string | null };
  };
  activity_log: Array<{
    action: string;
    entity_type: string;
    timestamp: string;
    details?: string;
  }>;
  data_retention_info: {
    note: string;
    how_to_export_business_data: string;
  };
}

export interface AccountDeletionRequest {
  email: string;
  password: string;
  totpCode?: string;
  otpCode?: string;
  challengeId?: string;
  requestId?: string;
  previousRequestId?: string;
  exportDataFirst?: boolean;
}
export type AccountDeletionMfa = { required: false } | { required: true; challengeId: string; method: 'email' | 'totp'; status: 'sent' | 'verified'; expiresAt: string };
export interface AccountDeletionRecord {
  request_id: string;
  status: 'pending' | 'blocked' | 'processing' | 'completed' | 'cancelled';
  requested_at: string;
  scheduled_at: string;
  cancellation_deadline: string;
  completed_at: string | null;
  blocker: string | null;
}
export interface AccountDeletionResult {
  request: AccountDeletionRecord | null;
  completed: boolean;
  local_erasure_authorized: boolean;
}
export class AccountDeletionClientError extends Error {
  constructor(public code: string) { super(code); this.name = 'AccountDeletionClientError'; }
}
const deletionIds = new Map<string, string>();
const deletionFlights = new Map<string, Promise<AccountDeletionResult>>();
const deletionMfaFlights = new Map<string, Promise<AccountDeletionMfa>>();
const deletionUUID = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
function deletionResponse(value: unknown): AccountDeletionResult {
  const v = value as AccountDeletionResult | null;
  if (!v || typeof v.completed !== 'boolean' || typeof v.local_erasure_authorized !== 'boolean') throw new AccountDeletionClientError('invalid_response');
  if (v.request === null) {
    if (v.completed || v.local_erasure_authorized) throw new AccountDeletionClientError('invalid_response');
    return v;
  }
  const row = v.request;
  if (!row || !deletionUUID(row.request_id) || !['pending', 'blocked', 'processing', 'completed', 'cancelled'].includes(row.status) ||
      ![row.requested_at, row.scheduled_at, row.cancellation_deadline].every(x => typeof x === 'string' && Number.isFinite(Date.parse(x))) ||
      !(row.blocker === null || typeof row.blocker === 'string') ||
      !(row.completed_at === null || typeof row.completed_at === 'string' && Number.isFinite(Date.parse(row.completed_at))) ||
      (row.status === 'completed' && !v.completed) || (v.completed !== (row.status === 'completed' && !!row.completed_at)) || (v.local_erasure_authorized && !v.completed)) throw new AccountDeletionClientError('invalid_response');
  return v;
}


function deletionMfaResponse(value: unknown): AccountDeletionMfa {
  const v = value as AccountDeletionMfa | null;
  if (v && v.required === false) return { required: false };
  if (!v || v.required !== true || !deletionUUID(v.challengeId) || !['email', 'totp'].includes(v.method) || !['sent', 'verified'].includes(v.status) || typeof v.expiresAt !== 'string' || !Number.isFinite(Date.parse(v.expiresAt))) throw new AccountDeletionClientError('invalid_mfa_response');
  return v;
}

class GDPRService {
  // ============================================
  // EXPORT DATA (Art. 20 GDPR - Portabilità)
  // ============================================

  /**
   * Esporta tutti i dati personali dell'utente in formato JSON
   * Rate limited: max 1 export al giorno
   */
  async exportUserData(): Promise<GDPRExportData> {
    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Utente non autenticato');

    const userId = userData.user.id;

    // Check rate limiting
    const canExport = await this.checkExportRateLimit(userId);
    if (!canExport) {
      throw new Error('Hai già effettuato un export oggi. Riprova domani.');
    }

    // Get user profile with organization
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select(`
        id,
        email,
        full_name,
        phone,
        avatar_url,
        profile_image_url,
        created_at,
        updated_at,
        organization_id,
        google_account_connected,
        privacy_policy_accepted_at,
        privacy_policy_version,
        terms_accepted_at,
        marketing_consent_email,
        marketing_consent_sms,
        marketing_consent_date,
        last_gdpr_export_at,
        gdpr_export_count
      `)
      .eq('id', userId)
      .single();

    if (profileError || !userProfile) {
      throw new Error('Impossibile recuperare il profilo utente');
    }

    // Get organization info
    let organizationData = null;
    if (userProfile.organization_id) {
      const { data: org } = await supabase
        .from('organizations')
        .select('id, name, created_at')
        .eq('id', userProfile.organization_id)
        .single();

      if (org) {
        // Get user role in organization
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role_id, roles(name)')
          .eq('user_id', userId)
          .eq('organization_id', userProfile.organization_id)
          .single();

        organizationData = {
          id: org.id,
          name: org.name,
          role: (roleData?.roles as any)?.name || 'member',
          joined_at: userProfile.created_at
        };
      }
    }

    // Get activity log (last 100 actions by this user)
    const { data: activityLogs } = await supabase
      .from('audit_logs')
      .select('action_type, entity_type, created_at, entity_name')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);

    // Build export data
    const exportData: GDPRExportData = {
      export_info: {
        date: new Date().toISOString(),
        format: 'GDPR_PORTABLE_JSON',
        version: '1.0',
        data_controller: 'Obly S.r.l.',
        data_controller_email: 'privacy@obly.it'
      },
      user: {
        id: userProfile.id,
        email: userProfile.email,
        full_name: userProfile.full_name,
        phone: userProfile.phone,
        avatar_url: userProfile.avatar_url || userProfile.profile_image_url,
        created_at: userProfile.created_at,
        updated_at: userProfile.updated_at
      },
      organization: organizationData,
      preferences: {
        google_account_connected: userProfile.google_account_connected || false
      },
      consents: {
        privacy_policy: {
          accepted: !!userProfile.privacy_policy_accepted_at,
          date: userProfile.privacy_policy_accepted_at,
          version: userProfile.privacy_policy_version
        },
        terms_of_service: {
          accepted: !!userProfile.terms_accepted_at,
          date: userProfile.terms_accepted_at
        },
        marketing_email: {
          accepted: userProfile.marketing_consent_email || false,
          date: userProfile.marketing_consent_date
        },
        marketing_sms: {
          accepted: userProfile.marketing_consent_sms || false,
          date: userProfile.marketing_consent_date
        }
      },
      activity_log: (activityLogs || []).map(log => ({
        action: log.action_type,
        entity_type: log.entity_type,
        timestamp: log.created_at,
        details: log.entity_name
      })),
      data_retention_info: {
        note: 'I dati aziendali (clienti, lead, trattative) non sono inclusi in questo export perché sono dati di terzi gestiti dalla tua organizzazione.',
        how_to_export_business_data: 'Per esportare i dati aziendali, vai su Impostazioni → Import/Export.'
      }
    };

    // Update export count and timestamp
    await this.updateExportStats(userId);

    // Log GDPR action
    await this.logGDPRAction(userId, 'DATA_EXPORT', 'Esportazione dati personali GDPR');

    return exportData;
  }

  /**
   * Scarica l'export come file JSON
   */
  async downloadExport(): Promise<void> {
    const data = await this.exportUserData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flusia-gdpr-export-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Verifica se l'utente può fare un export (rate limiting 1/giorno)
   */
  private async checkExportRateLimit(userId: string): Promise<boolean> {
    const { data } = await supabase
      .from('user_profiles')
      .select('last_gdpr_export_at')
      .eq('id', userId)
      .single();

    if (!data?.last_gdpr_export_at) return true;

    const lastExport = new Date(data.last_gdpr_export_at);
    const now = new Date();
    const hoursSinceLastExport = (now.getTime() - lastExport.getTime()) / (1000 * 60 * 60);

    return hoursSinceLastExport >= 24;
  }

  /**
   * Aggiorna statistiche export
   */
  private async updateExportStats(userId: string): Promise<void> {
    try {
      // Get current count
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('gdpr_export_count')
        .eq('id', userId)
        .single();

      const currentCount = profile?.gdpr_export_count || 0;

      // Update with incremented count
      await supabase
        .from('user_profiles')
        .update({
          last_gdpr_export_at: new Date().toISOString(),
          gdpr_export_count: currentCount + 1
        })
        .eq('id', userId);
    } catch (error) {
      console.error('[GDPR] Error updating export stats:', error);
      // At minimum, update the timestamp
      await supabase
        .from('user_profiles')
        .update({
          last_gdpr_export_at: new Date().toISOString()
        })
        .eq('id', userId);
    }
  }

  // ============================================
  // DELETE ACCOUNT (Art. 17 GDPR - Diritto all'Oblio)
  // ============================================

  /** IDs contain no credentials. Retained for retries after uncertain responses. */
  private deletionOperationId(userId: string, action: string, target: string): string {
    const key = `flusia.account-deletion.v1:${userId}:${action}:${target}`;
    let id = deletionIds.get(key);
    if (!id && typeof window !== 'undefined') {
      try { const saved = window.sessionStorage.getItem(key); if (deletionUUID(saved)) id = saved; } catch { /* Session memory remains available. */ }
    }
    if (!id) id = crypto.randomUUID();
    deletionIds.set(key, id);
    if (typeof window !== 'undefined') { try { window.sessionStorage.setItem(key, id); } catch { /* Never persist passwords or codes. */ } }
    return id;
  }

  private async deletionActor() {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user?.id || !data.user.email) throw new AccountDeletionClientError('unauthorized');
    return { id: data.user.id, email: data.user.email };
  }

  private async deletionRawCall(path: string, method: 'GET' | 'POST', actorId: string, body?: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await coreCall<unknown>(path, { method, ...(body ? { body } : {}) });
    const { data: current, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || current.session?.user.id !== actorId) throw new AccountDeletionClientError('scope_changed');
    if (error) {
      // Router errors are static strings; never surface a raw upstream response or credential.
      const code = error.code || (/^[a-z][a-z_]+$/.test(error.message) ? error.message : 'account_deletion_unavailable');
      throw new AccountDeletionClientError(code);
    }
    return data;
  }

  private async deletionCall(path: string, method: 'GET' | 'POST', actorId: string, body?: Record<string, unknown>): Promise<AccountDeletionResult> {
    return deletionResponse(await this.deletionRawCall(path, method, actorId, body));
  }

  private async deletionMutation(action: 'request' | 'cancel', request: AccountDeletionRequest, target?: string): Promise<AccountDeletionResult> {
    const actor = await this.deletionActor();
    if (request.email.trim().toLowerCase() !== actor.email.toLowerCase()) throw new AccountDeletionClientError('email_mismatch');
    if (!request.password || request.password.length > 1024 || request.totpCode && !/^[0-9]{6}$/.test(request.totpCode) || request.otpCode && !/^[0-9]{6}$/.test(request.otpCode) || request.challengeId && !deletionUUID(request.challengeId)) throw new AccountDeletionClientError('invalid_credentials');
    if (action === 'cancel' && !deletionUUID(target)) throw new AccountDeletionClientError('invalid_request');
    const operationTarget = action === 'cancel' ? target! : request.previousRequestId || 'initial';
    const requestId = request.requestId ?? this.deletionOperationId(actor.id, action, operationTarget);
    if (!deletionUUID(requestId)) throw new AccountDeletionClientError('invalid_request');
    const key = `${actor.id}:${action}:${operationTarget}`;
    const existing = deletionFlights.get(key);
    if (existing) return existing;
    const call = this.deletionCall(action === 'cancel' ? 'account-deletion/cancel' : 'account-deletion', 'POST', actor.id, {
      request_id: requestId, email: actor.email, password: request.password,
      ...(request.totpCode ? { totp_code: request.totpCode } : {}),
      ...(request.otpCode ? { otp_code: request.otpCode } : {}),
      ...(request.challengeId ? { challenge_id: request.challengeId } : {}),
      ...(action === 'cancel' ? { deletion_request_id: target } : {}),
    }).then(result => {
      if (!result.request || action === 'request' && result.request.request_id !== requestId || action === 'cancel' && (result.request.request_id !== target || result.request.status !== 'cancelled')) throw new AccountDeletionClientError('invalid_response');
      return result;
    });
    deletionFlights.set(key, call);
    try { return await call; } finally { if (deletionFlights.get(key) === call) deletionFlights.delete(key); }
  }

  /** Challenge only: a delivered OTP is never a deletion receipt. Same operation UUID as final submit. */
  async beginAccountDeletionMfa(action: 'request' | 'cancel', request: AccountDeletionRequest & { deletionRequestId?: string }, resend = false): Promise<AccountDeletionMfa> {
    const actor = await this.deletionActor();
    if (request.email.trim().toLowerCase() !== actor.email.toLowerCase()) throw new AccountDeletionClientError('email_mismatch');
    if (!request.password || request.password.length > 1024 || action === 'cancel' && !deletionUUID(request.deletionRequestId)) throw new AccountDeletionClientError('invalid_credentials');
    const target = action === 'cancel' ? request.deletionRequestId! : request.previousRequestId || 'initial';
    const requestId = request.requestId ?? this.deletionOperationId(actor.id, action, target);
    if (!deletionUUID(requestId)) throw new AccountDeletionClientError('invalid_request');
    const key = `${actor.id}:${action}:${target}`;
    const existing = deletionMfaFlights.get(key); if (existing) return existing;
    const call = this.deletionRawCall('account-deletion/mfa', 'POST', actor.id, {
      request_id: requestId, action, email: actor.email, password: request.password, resend,
      ...(action === 'cancel' ? { deletion_request_id: request.deletionRequestId } : {}),
    }).then(deletionMfaResponse);
    deletionMfaFlights.set(key, call);
    try { return await call; } finally { if (deletionMfaFlights.get(key) === call) deletionMfaFlights.delete(key); }
  }

  /** Durable initiation only; server owns status/dates/execution. Never soft-delete locally. */
  requestAccountDeletion(request: AccountDeletionRequest): Promise<AccountDeletionResult> {
    return this.deletionMutation('request', request);
  }

  cancelAccountDeletion(request: AccountDeletionRequest & { deletionRequestId: string }): Promise<AccountDeletionResult> {
    return this.deletionMutation('cancel', request, request.deletionRequestId);
  }

  async getDeletionStatus(): Promise<AccountDeletionResult> {
    const actor = await this.deletionActor();
    return this.deletionCall('account-deletion', 'GET', actor.id);
  }

  /**
   * Ottiene le info sull'ultimo export
   */
  async getExportInfo(): Promise<{
    lastExportAt: string | null;
    exportCount: number;
    canExportToday: boolean;
  }> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Utente non autenticato');

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('last_gdpr_export_at, gdpr_export_count')
      .eq('id', userData.user.id)
      .single();

    const canExport = await this.checkExportRateLimit(userData.user.id);

    return {
      lastExportAt: profile?.last_gdpr_export_at || null,
      exportCount: profile?.gdpr_export_count || 0,
      canExportToday: canExport
    };
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Log azione GDPR nell'audit log
   */
  private async logGDPRAction(userId: string, action: string, details: string): Promise<void> {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id, email, full_name')
        .eq('id', userId)
        .single();

      if (profile?.organization_id) {
        await supabase.from('audit_logs').insert({
          organization_id: profile.organization_id,
          user_id: userId,
          user_email: profile.email,
          user_name: profile.full_name || profile.email,
          entity_type: 'user',
          entity_id: userId,
          entity_name: 'GDPR Action',
          action_type: 'gdpr',
          field_name: action,
          new_value: details,
          source_system: 'gdpr_service'
        });
      }
    } catch (error) {
      console.error('[GDPR] Error logging action:', error);
    }
  }

  /**
   * Verifica se l'utente ha 2FA attivo
   */
  async has2FAEnabled(): Promise<boolean> {
    const { data: factors, error } = await supabase.auth.mfa.listFactors();
    if (error || !factors) throw new AccountDeletionClientError('mfa_unavailable');
    return factors.totp.some(factor => factor.status === 'verified');
  }
}

export const gdprService = new GDPRService();
